import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
export let lastModified = undefined;
export let frontmatter = {
  "title": "Recursive `turbo` invocations",
  "description": "Learn more about errors with recursive scripts and tasks in Turborepo."
};
export let structuredData = {
  "contents": [{
    "heading": "why-this-error-occurred",
    "content": "When a cycle of turbo invocations is detected in a single-package workspace, Turborepo will error to prevent the recursive calls to itself. Typically, this situation occurs for one of two reasons:"
  }, {
    "heading": "recursion-in-scripts-and-tasks",
    "content": "In a single-package workspace, a script in package.json that calls a Turborepo task with the same name causes a loop."
  }, {
    "heading": "recursion-in-scripts-and-tasks",
    "content": "Calling the build script calls turbo run build. turbo run build then calls the build script, initiating the loop of recursive calls."
  }, {
    "heading": "recursion-in-scripts-and-tasks",
    "content": "To resolve this, ensure that the name of the script in package.json is not the same as the Turborepo task. For example, to fix the snippet above, renaming the script would break the cycle:"
  }, {
    "heading": "package-manager-workspace-misconfiguration",
    "content": "A misconfigured workspace can make it appear that a multi-package workspace is a single-package workspace. This causes Turborepo to infer that the repository is of the wrong type, causing it to see the script in package.json to be recursive."
  }, {
    "heading": "package-manager-workspace-misconfiguration",
    "content": "Your repo can end up in this state in a few ways, with the most common being that the packages are not defined according to your package manager. An npm workspace that is missing the workspaces field in package.json or a pnpm workspace that is missing a pnpm-workspace.yaml file can result in this error message."
  }, {
    "heading": "package-manager-workspace-misconfiguration",
    "content": "Check that your repository is complying with standards for multi-package workspaces and correct any issues."
  }],
  "headings": [{
    "id": "why-this-error-occurred",
    "content": "Why this error occurred"
  }, {
    "id": "recursion-in-scripts-and-tasks",
    "content": "Recursion in scripts and tasks"
  }, {
    "id": "package-manager-workspace-misconfiguration",
    "content": "Package manager Workspace misconfiguration"
  }]
};
export const toc = [{
  depth: 2,
  url: "#why-this-error-occurred",
  title: _jsx(_Fragment, {
    children: "Why this error occurred"
  })
}, {
  depth: 3,
  url: "#recursion-in-scripts-and-tasks",
  title: _jsx(_Fragment, {
    children: "Recursion in scripts and tasks"
  })
}, {
  depth: 3,
  url: "#package-manager-workspace-misconfiguration",
  title: _jsx(_Fragment, {
    children: "Package manager Workspace misconfiguration"
  })
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h2: "h2",
    h3: "h3",
    p: "p",
    pre: "pre",
    span: "span",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      id: "why-this-error-occurred",
      children: "Why this error occurred"
    }), "\n", _jsxs(_components.p, {
      children: ["When a cycle of ", _jsx(_components.code, {
        children: "turbo"
      }), " invocations is detected in a ", _jsx(_components.a, {
        href: "https://turborepo.com/docs/guides/single-package-workspaces",
        children: "single-package workspace"
      }), ", Turborepo will error to prevent the recursive calls to itself. Typically, this situation occurs for one of two reasons:"]
    }), "\n", _jsx(_components.h3, {
      id: "recursion-in-scripts-and-tasks",
      children: "Recursion in scripts and tasks"
    }), "\n", _jsxs(_components.p, {
      children: ["In a single-package workspace, a script in ", _jsx(_components.code, {
        children: "package.json"
      }), " that calls a Turborepo task with the same name causes a loop."]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./package.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"scripts\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"turbo run build\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Calling the ", _jsx(_components.code, {
        children: "build"
      }), " script calls ", _jsx(_components.code, {
        children: "turbo run build"
      }), ". ", _jsx(_components.code, {
        children: "turbo run build"
      }), " then calls the ", _jsx(_components.code, {
        children: "build"
      }), " script, initiating the loop of recursive calls."]
    }), "\n", _jsxs(_components.p, {
      children: ["To resolve this, ensure that the name of the script in ", _jsx(_components.code, {
        children: "package.json"
      }), " is not the same as the Turborepo task. For example, to fix the snippet above, renaming the script would break the cycle:"]
    }), "\n", _jsx(_Fragment, {
      children: _jsx(_components.pre, {
        className: "shiki shiki-themes css-variables css-variables",
        style: {
          "--shiki-light": "var(--shiki-foreground)",
          "--shiki-dark": "var(--shiki-foreground)",
          "--shiki-light-bg": "var(--shiki-background)",
          "--shiki-dark-bg": "var(--shiki-background)"
        },
        tabIndex: "0",
        title: "./package.json",
        icon: "<svg viewBox=\"0 0 24 24\"><path d=\"M 6,1 C 4.354992,1 3,2.354992 3,4 v 16 c 0,1.645008 1.354992,3 3,3 h 12 c 1.645008,0 3,-1.354992 3,-3 V 8 7 A 1.0001,1.0001 0 0 0 20.707031,6.2929687 l -5,-5 A 1.0001,1.0001 0 0 0 15,1 h -1 z m 0,2 h 7 v 3 c 0,1.645008 1.354992,3 3,3 h 3 v 11 c 0,0.564129 -0.435871,1 -1,1 H 6 C 5.4358712,21 5,20.564129 5,20 V 4 C 5,3.4358712 5.4358712,3 6,3 Z M 15,3.4140625 18.585937,7 H 16 C 15.435871,7 15,6.5641288 15,6 Z\" fill=\"currentColor\" /></svg>",
        children: _jsxs(_components.code, {
          children: [_jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "{"
            })
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "  \"scripts\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: " {"
            })]
          }), "\n", _jsxs(_components.span, {
            className: "line",
            children: [_jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-keyword)",
                "--shiki-dark": "var(--shiki-token-keyword)"
              },
              children: "    \"build:app\""
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-punctuation)",
                "--shiki-dark": "var(--shiki-token-punctuation)"
              },
              children: ":"
            }), _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-token-string-expression)",
                "--shiki-dark": "var(--shiki-token-string-expression)"
              },
              children: " \"turbo run build\""
            })]
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "  }"
            })
          }), "\n", _jsx(_components.span, {
            className: "line",
            children: _jsx(_components.span, {
              style: {
                "--shiki-light": "var(--shiki-foreground)",
                "--shiki-dark": "var(--shiki-foreground)"
              },
              children: "}"
            })
          })]
        })
      })
    }), "\n", _jsx(_components.h3, {
      id: "package-manager-workspace-misconfiguration",
      children: "Package manager Workspace misconfiguration"
    }), "\n", _jsxs(_components.p, {
      children: ["A misconfigured workspace can make it appear that a ", _jsx(_components.a, {
        href: "https://vercel.com/docs/vercel-platform/glossary#multi-package-workspace",
        children: "multi-package workspace"
      }), " is a single-package workspace. This causes Turborepo to infer that the repository is of the wrong type, causing it to see the script in ", _jsx(_components.code, {
        children: "package.json"
      }), " to be recursive."]
    }), "\n", _jsxs(_components.p, {
      children: ["Your repo can end up in this state in a few ways, with the most common being that the ", _jsx(_components.a, {
        href: "https://turborepo.com/docs/crafting-your-repository/structuring-a-repository#specifying-packages-in-a-monorepo",
        children: "packages are not defined according to your package manager"
      }), ". An npm workspace that is missing the ", _jsx(_components.code, {
        children: "workspaces"
      }), " field in ", _jsx(_components.code, {
        children: "package.json"
      }), " or a pnpm workspace that is missing a ", _jsx(_components.code, {
        children: "pnpm-workspace.yaml"
      }), " file can result in this error message."]
    }), "\n", _jsx(_components.p, {
      children: "Check that your repository is complying with standards for multi-package workspaces and correct any issues."
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
